import { useState } from "react";
// import { base64FromPath } from "@ionic/react-hooks/filesystem";
import { Camera, CameraDirection, CameraResultType, CameraSource, ImageOptions, GalleryImageOptions } from "@capacitor/camera";
import { Capacitor } from "@capacitor/core";
import heic2any from "heic2any";
import { base64FromPath } from "../utils/base64Util";

export interface Photo {
  filepath: string;
  webviewPath?: string;
  base64?: string;
  isImage?: boolean;
  fileType?: string;
  uri?: string;
  dimensions?: any;
  isLandscape?: boolean;
  ratio?: number;
  selectedSource?: string;
}

export function usePhotoGallery() {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [state, setState] = useState(false);
  const [selectedSource, setSelectedSource] = useState("");

  const  savePicture = async (photo: any, fileName: string, isImage = true, fileType = "image/jpeg"): Promise<Photo> => {
    let webPath = '';
    let base64Data: any = '';
    
    if(fileType.includes("image")) {
      webPath = Capacitor.convertFileSrc(photo);
      base64Data = await base64FromPath(webPath);
      console.log({fileType})
      // Strict HEIC/HEIF detection: rely on provided fileType first, then exact DataURL mime
      const lowerType = (fileType || '').toLowerCase();
      const isHeic = lowerType.includes('heic') || lowerType.includes('heif')
        || (typeof base64Data === 'string' && (base64Data.startsWith('data:image/heic') || base64Data.startsWith('data:image/heif')));
      if(isHeic) {
        try {
          // Convert HEIC to JPEG for Android and iOS compatibility
          const response = await fetch(webPath);
          const blob = await response.blob();
          const heicblob = await heic2any({ blob, toType: "image/jpeg", quality: 0.5 });
          base64Data = await base64FromPath(webPath, heicblob);
          isImage = true;
          fileType = "image/jpeg";
        } catch (e: any) {
          // If already browser readable (e.g., JPEG mistaken as HEIC), continue without converting
          if (e && (e.message || '').toString().includes('already browser readable')) {
            // noop: keep original base64Data and fileType
          } else {
            throw e;
          }
        }
      }
      let { dimensions, isLandscape, ratio } = await getImageDimension(base64Data);
      return {
        filepath: fileName, webviewPath: webPath, base64: base64Data, isImage, fileType, uri: photo, dimensions: dimensions, isLandscape: isLandscape, ratio: ratio,
        selectedSource: selectedSource
      };
    } 

    webPath = '';
    base64Data = photo;
    let { dimensions, isLandscape, ratio } = {dimensions: 0, isLandscape: false, ratio: 0,}
    return {
      filepath: fileName, webviewPath: webPath, base64: base64Data, isImage, fileType, uri: photo, dimensions: dimensions, isLandscape: isLandscape, ratio: ratio,
      selectedSource: selectedSource
    };
  };

  const getImageDimension = (base64Data: string): Promise<any> => {
    return new Promise((resolve, reject) => {
      const im = new Image();
      im.src = base64Data;
      const dimensions = { width: im.width, height: im.height };
      let ratio = 1;
      let isLandscape = false;
      im.onload = () => {
        dimensions.width = im.width;
        dimensions.height = im.height;
        isLandscape = dimensions.width > dimensions.height;
        if (isLandscape) {
          ratio = dimensions.height / dimensions.width;
        } else {
          ratio = dimensions.width / dimensions.height;
        }
        console.log({ dimensions, width: window.innerWidth, height: window.innerHeight, isLandscape, ratio });
        resolve({
          dimensions: dimensions,
          isLandscape: isLandscape,
          ratio: ratio,
        });
      };
    });
  };

  const takePhoto = async (bol: any, selectedOption: any, files: any = []): Promise<void> => {
    console.log({selectedOption})
    if (selectedOption === "Camera") {
      const options: ImageOptions = {
        quality: 50,
        resultType: CameraResultType.Uri,
        source: CameraSource.Camera,
        direction: CameraDirection.Front,
        correctOrientation: true,
      };
      return Camera.getPhoto(options)
        .then(
          async (imageData: any) => {
            console.log({imageData})
            const fileName = new Date().getTime() + ".jpeg";
            const savedFileImage = await savePicture(imageData.webPath, fileName);
            let newPhotos: Photo[] = [];
            newPhotos = [savedFileImage, ...newPhotos];
            console.log({newPhotos})
            await setState(true);
            await setPhotos(newPhotos);
            setSelectedSource(selectedOption);
          }
        )
        .catch((err: any) => {
          console.error(err);
          throw err;
        });
    } else if (selectedOption === "Gallery") {
      const options: GalleryImageOptions = {
        quality: 50,
        correctOrientation: true,
        limit: 3000
      };
      return Camera.pickImages(options)
        .then(
          async (results: any) => {
            console.log({results})
            const selected = results && Array.isArray(results.photos) ? results.photos : [];
            if (selected.length === 0) {
              throw new Error('No photos selected');
            }
            const newPhotos: Photo[] = [];
            let failedCount = 0;
            for (let i = 0; i < selected.length; i++) {
              const imageData = selected[i];
              try {
                const sourcePath = imageData.webPath || imageData.path;
                const ext = (imageData.format || (sourcePath ? (sourcePath.split('.').pop() || '').toLowerCase() : '') || 'jpeg');
                const mime = ext.startsWith('image/') ? ext : `image/${ext}`;
                const fileName = new Date().getTime() + "." + (ext.replace('image/', ''));
                const savedFileImage = await savePicture(sourcePath, fileName, true, mime);
                newPhotos.push(savedFileImage);
              } catch (e) {
                console.error('Failed to process selected image', e);
                failedCount++;
              }
              // Yield between items to keep UI responsive on lower-end devices
              // eslint-disable-next-line no-await-in-loop
              await Promise.resolve();
            }
            console.log({ newPhotos, failedCount })
            if (newPhotos.length === 0) {
              throw new Error('Failed to process selected photos');
            }
            await setState(true);
            await setPhotos(newPhotos);
            setSelectedSource(selectedOption);
          }
        )
        .catch((err: any) => {
          console.error(err);
          throw err;
        });

    } else {
      // file chooser
        return new Promise<void>((resolve, reject) => {
          try {
            let newPhotos: Photo[] = [];
            if (!files || files.length === 0) {
              resolve();
              return;
            }
            for (let i = 0; i < files.length; i++) {
              const file = files[i];
  
              const fileReader = new FileReader();
              fileReader.readAsDataURL(file);
              // eslint-disable-next-line no-loop-func
              fileReader.onload = async () => {
                const fileName = new Date().getTime() + "-" + file.name;
                let isImage = file.type.includes("image");
                if(file.type.includes("heic") || file.type.includes("heif")) {
                  isImage = false;
                }
                const savedFileImage = await savePicture(fileReader.result, fileName, isImage, file.type);
                console.log({savedFileImage})
                newPhotos = [savedFileImage, ...newPhotos];
  
                if(i === (files.length - 1)) {
                  console.log({ newPhotos });
                  setState(true);
                  setPhotos(newPhotos);
                  setSelectedSource(selectedOption);
                  console.log("==============================Documents Picker - Length=======================", newPhotos.length);
                  resolve();
                }
              };
              fileReader.onerror = (e) => {
                reject(e);
              }
            }
          } catch (e) {
            reject(e);
          }
        });
    }
  };

  return {
    takePhoto,
    photos,
    state,
    selectedSource,
  };
}

